// @givmo/app-charity — accept charity-admin invite (CPA-WEB2). The INVITEE side.
//
// The invitee lands here from the emailed invite link (`/invites/accept?token=…`). The accept endpoint
// (`POST /charity-admin/invites/accept`, body `{ token }`) is AUTHENTICATED — it binds the (email-bound,
// single-use) invite to the CURRENT signed-in user — so this route sits behind the auth guard: a
// signed-out invitee is sent to the CREATE-ACCOUNT door, /sign-up, first (D-CP-65 — a colleague
// invited onto the admin team is more likely new to Givmo; an account holder taps "Sign in" there),
// and the guard's `redirect` carries this address — token and all — back here after account
// creation + verification, or after sign-in. Once signed in, we auto-submit the token and branch
// honestly:
//   - success  → they're now a member; the one control goes through the root doorway (the landing
//                rule decides where this person's console opens — CPA-WEB41 retired the legacy
//                `/admins` page, and there is no redirect to send them to).
//   - invalid / expired / already-accepted / wrong-email → an honest, retry-free error state (the
//     backend's own message when present, else a plain fallback), with a way back to the app grid.
//   - missing token → an explicit "invalid link" state (no call fired).
//
// The root keeps `data-testid="page-accept-invite"`.

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { sdk, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { CheckCircle2, LayoutGrid, MailWarning } from 'lucide-react';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../auth/auth-routes';
import { charityAdminListMyMembershipsOptions } from '@givmo/api-client/query';

const PREFIX = 'accept-invite';

/** The redeem's cache key: the TOKEN, and nothing else — it is what the answer belongs to. */
function acceptInviteKey(token: string): readonly unknown[] {
  return ['charity-admin-invite-redeem', token] as const;
}

export function isTransientInviteError(error: unknown): boolean {
  return (
    isGivmoError(error) &&
    (error.kind === 'network' ||
      error.kind === 'server' ||
      error.status === 408 ||
      error.status === 429 ||
      (error.status != null && error.status >= 500))
  );
}

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Registered under the pathless `authenticated` layout → id `/authenticated/invites/accept`.
  const { token } = useSearch({ from: '/authenticated/invites/accept' });

  // ⚠ THE REDEEM IS A QUERY KEYED BY THE TOKEN, BECAUSE THIS PAGE REMOUNTS WHILE IT IS IN FLIGHT.
  // A `useMutation` submitted from a mount effect cannot survive that: its result belongs to the
  // observer that issued it, so a teardown discards the answer, and the fresh instance that replaces
  // it re-fires the effect with a token the server has already spent. Both measured on this page:
  //
  //   * **Production.** `PortalEntryGate` (`src/org/claim-first-gate.tsx`) chooses the CHROME from
  //     the memberships read, and an invitee administers nothing yet — so the read is PENDING when
  //     this page mounts and submits (entry `portal`, `<AppShell>`), answers EMPTY a moment later
  //     (entry `resolving`, which renders its own frame INSTEAD of its children, unmounting this
  //     page), then settles (entry `onboarding`, `<OnboardingShell>`, this page back again because
  //     `/invites/accept` is a claim-first path). Measured: TWO POSTs of one single-use token, the
  //     second refused — the invitee told the invite was invalid or already used, after it was in
  //     fact accepted.
  //   * **Development.** `main.tsx` wraps the app in `<StrictMode>`, whose mount → teardown →
  //     remount reaches the same teardown by a shorter road. Measured: one POST, then
  //     `loading=true, success=false, error=false` — the skeleton, forever.
  //
  // A fire-once ref cannot fix either: it is per-instance, so it does not survive the remount that
  // causes this, and dropping it only makes the double-submit unconditional. The CACHE is what
  // carries the answer across a remount, which is what a query has and a mutation does not — and it
  // is also what makes the second mount join the first mount's in-flight request instead of opening
  // a second one.
  //
  // The retry/refetch options below are what keep a spent token from ever being re-submitted on its
  // own: no automatic retries, and no refetch on a remount, a window focus or a reconnect. Asking
  // again is only ever an explicit act (`refetch`), and only offered where the answer could change.
  // The abort signal is deliberately NOT threaded into the call: a teardown would then cancel the
  // in-flight POST, and the backend may already have consumed the token by that point.
  const acceptToken = typeof token === 'string' ? token : '';
  const accept = useQuery({
    queryKey: acceptInviteKey(acceptToken),
    enabled: acceptToken.length > 0,
    queryFn: async () => {
      const { data } = await sdk.charityAdminAcceptCharityInvite({
        body: { token: acceptToken },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retryOnMount: false,
  });

  // The memberships read now answers wrongly — it was taken before this person had this membership —
  // so it is invalidated rather than refetched: whoever reads it next asks for themselves.
  //
  // The toast is announced at most once, and only by a mount that saw the answer ARRIVE
  // (`isFetchedAfterMount`). ⚠ The invalidation above is what remounts this page (Org Machinery
  // OM-79): the memberships read answers, `PortalEntryGate` swaps the chrome, and the page mounts
  // again with the answer already in the cache — and a fresh mount's ref is fresh too, so the accept
  // was announced twice. That remount reads the cached answer and never fetches, so it says nothing,
  // as a return trip to this page does; a mount that joined an in-flight redeem sees it land and
  // announces it. The ref still guards StrictMode's double-invoke of one mount's effect.
  const announcedRef = useRef(false);
  useEffect(() => {
    if (!accept.isSuccess) return;
    void queryClient.invalidateQueries({
      queryKey: charityAdminListMyMembershipsOptions().queryKey,
    });
    if (!accept.isFetchedAfterMount || announcedRef.current) return;
    announcedRef.current = true;
    toast.success('Invite accepted');
  }, [accept.isSuccess, accept.isFetchedAfterMount, queryClient]);

  // ── Missing / empty token → an explicit invalid-link state (no call fired). ──────────────────────
  if (typeof token !== 'string' || token.length === 0) {
    return (
      <AcceptShell>
        <ResultCard
          testId={`${PREFIX}-invalid`}
          tone="warning"
          icon={MailWarning}
          title="This invite link isn’t valid"
          description="The link is missing its invite code. Ask an admin to send you a fresh invite."
          onDashboard={() => void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE })}
        />
      </AcceptShell>
    );
  }

  // ── Error → honest, retry-free (the invite is single-use; retrying the same token won't help). ───
  if (accept.isError) {
    const transient = isTransientInviteError(accept.error);
    return (
      <AcceptShell>
        <ResultCard
          testId={`${PREFIX}-error`}
          tone="warning"
          icon={MailWarning}
          title={transient ? 'We couldn’t reach Givmo' : 'We couldn’t accept this invite'}
          description={
            isGivmoError(accept.error)
              ? accept.error.message
              : 'This invite may have expired, already been used, or be addressed to a different email. Ask an admin for a new invite.'
          }
          primaryLabel={transient ? 'Retry' : undefined}
          onPrimary={transient ? () => void accept.refetch() : undefined}
          onDashboard={() => void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE })}
        />
      </AcceptShell>
    );
  }

  // ── Success → they're now a member; the doorway control is the way in (no admins page to offer). ──
  if (accept.isSuccess) {
    const orgName = accept.data?.name;
    return (
      <AcceptShell>
        <ResultCard
          testId={`${PREFIX}-success`}
          tone="success"
          icon={CheckCircle2}
          title={orgName ? `You’ve joined ${orgName}` : 'You’ve joined the organization'}
          description="You now have access to administer this organization on Givmo."
          onDashboard={() => void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE })}
        />
      </AcceptShell>
    );
  }

  // ── Pending (auto-submitting) → a lightweight loading card. ──────────────────────────────────────
  return (
    <AcceptShell>
      <Card className="p-24" testId={`${PREFIX}-loading`}>
        <div className="flex flex-col gap-10">
          <Skeleton className="h-16 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </Card>
    </AcceptShell>
  );
}

function AcceptShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[560px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-accept-invite')}
    >
      <PageHeader
        title="Accept your invite"
        subtitle="Join an organization's admin team on Givmo."
      />
      {children}
    </div>
  );
}

interface ResultCardProps {
  testId: string;
  tone: 'success' | 'warning';
  icon: typeof CheckCircle2;
  title: string;
  description: string;
  primaryLabel?: string;
  onPrimary?: () => void;
  onDashboard: () => void;
}

function ResultCard({
  testId,
  tone,
  icon,
  title,
  description,
  primaryLabel,
  onPrimary,
  onDashboard,
}: ResultCardProps) {
  const ring =
    tone === 'success' ? 'bg-success-50 text-success-600' : 'bg-warning-50 text-warning-600';
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col items-center gap-16 px-24 py-32 text-center">
        <span className={`flex size-[56px] items-center justify-center rounded-full ${ring}`}>
          <Icon icon={icon} size={28} aria-hidden />
        </span>
        <div className="flex flex-col gap-6">
          <h2 className="font-w700 text-h5 text-text-strong">{title}</h2>
          <p className="max-w-[420px] text-s14 text-text-secondary">{description}</p>
        </div>
        <div className="flex items-center gap-8">
          {primaryLabel && onPrimary ? (
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              onClick={onPrimary}
              testId={`${testId}-primary`}
            >
              {primaryLabel}
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone={primaryLabel ? 'secondary' : 'primary'}
            size="md"
            leadingIcon={<Icon icon={LayoutGrid} size={16} aria-hidden />}
            onClick={onDashboard}
            testId={`${testId}-dashboard`}
          >
            Go to your apps
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
